"use client";

import type { ButtonHTMLAttributes, CSSProperties } from "react";

/**
 * Button — the DelftInkoop primary action control.
 * Variants map to brand intent: primary (Delft blue), gold (florijn CTA),
 * outline/ghost for secondary actions, onDark for use over the blue hero.
 * Ported from the design system bundle (components/buttons/Button.jsx).
 */
export type ButtonVariant = "primary" | "gold" | "secondary" | "outline" | "ghost" | "onDark";
export type ButtonSize = "sm" | "md" | "lg";

const padding: Record<ButtonSize, string> = {
  sm: "8px 16px",
  md: "13px 22px",
  lg: "15px 26px",
};

const fontSize: Record<ButtonSize, number> = {
  sm: 13,
  md: 14.5,
  lg: 15,
};

const variants: Record<ButtonVariant, CSSProperties> = {
  primary: { background: "var(--color-primary)", color: "var(--color-on-primary)", border: "1px solid transparent" },
  gold: { background: "var(--gold-400)", color: "var(--gold-ink)", border: "1px solid transparent" },
  secondary: { background: "var(--surface-2)", color: "var(--ink-700)", border: "1px solid transparent" },
  outline: { background: "#fff", color: "var(--color-primary)", border: "1px solid var(--border-strong)" },
  ghost: { background: "transparent", color: "var(--color-primary)", border: "1px solid transparent" },
  onDark: { background: "#fff", color: "var(--color-primary)", border: "1px solid transparent" },
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
};

export function Button({ variant = "primary", size = "md", full = false, disabled = false, type = "button", style, children, ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      style={{
        font: "inherit",
        fontFamily: "var(--font-sans)",
        fontWeight: 700,
        fontSize: fontSize[size],
        lineHeight: 1.1,
        padding: padding[size],
        borderRadius: "var(--radius-lg)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        width: full ? "100%" : "auto",
        whiteSpace: "nowrap",
        transition: "filter var(--transition-fast), transform var(--transition-fast)",
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}
